import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Check, Clock, AlertCircle, ArrowRight, ArrowLeft, ChevronDown, ChevronUp,
  Info, Zap, Shield, Star, Package, BookOpen, Truck, Users, Sparkles, Gift, Film
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// BackButton component (inline)
const BackButton = ({ to, label = "Back" }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => to ? navigate(to) : navigate(-1)}
      className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-500 transition-colors mb-6 group"
      data-testid="back-button"
    >
      <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
      <span>{label}</span>
    </button>
  );
};

// RushBadge component (inline)
const RushBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full border border-red-500/30">
    <Zap size={12} /> RUSH
  </span>
);

// VideoBookBadge component (inline)
const VideoBookBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full border border-purple-500/30">
    <BookOpen size={12} /> VIDEO BOOK
  </span>
);

// This component receives useAuth as a prop from App.js
const CheckoutPage = ({ useAuth }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState(null);
  const [ordersPaused, setOrdersPaused] = useState(false);
  const [pauseMessage, setPauseMessage] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(searchParams.get("package") || "tier2");
  const [customPhotos, setCustomPhotos] = useState(10);
  const [rushDelivery, setRushDelivery] = useState(false);
  const [songRequest, setSongRequest] = useState(false);
  const [videoBook, setVideoBook] = useState(false);
  const [songDetails, setSongDetails] = useState("");
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [referralValidated, setReferralValidated] = useState(null);
  const [funeralHome, setFuneralHome] = useState("");
  const [arrangerName, setArrangerName] = useState("");
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [resumeOrderId, setResumeOrderId] = useState(null);

  // Calculate estimated delivery date
  const getEstimatedDelivery = (isRush = false) => {
    const today = new Date();
    const days = isRush ? 2 : 5; // Rush = 48 hours (2 days), Standard = 5 days
    const delivery = new Date(today);
    delivery.setDate(delivery.getDate() + days);
    return delivery.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const [formData, setFormData] = useState({
    customer_name: user?.full_name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  // Shipping address for Video Book delivery
  const [shippingAddress, setShippingAddress] = useState({
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "United States",
  });
  const VIDEO_BOOK_PRICE = 500;
  const VIDEO_BOOK_SHIPPING = 25; // Flat rate for 1.5 lb package
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [couponValid, setCouponValid] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);

  const fetchData = async () => {
    try {
      const [packagesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/packages`),
        axios.get(`${API}/settings/public`)
      ]);
      setPackages(packagesRes.data);
      setOrdersPaused(settingsRes.data.orders_paused || false);
      setPauseMessage(settingsRes.data.pause_message || "We are currently not accepting new orders. Please check back soon!");
      setIsTestMode(settingsRes.data.memorial_test_mode || false);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  // Handle resume order from abandoned cart email
  const handleResumeOrder = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/resume/${orderId}`);
      const order = res.data;
      
      // Pre-fill form with order data
      setFormData({
        customer_name: order.customer_name || "",
        customer_email: order.customer_email || "",
        customer_phone: order.customer_phone || "",
        timezone: order.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      
      // Set package selection
      if (order.package_id) {
        setSelectedPackage(order.package_id);
        if (order.package_id === "custom" && order.custom_photos) {
          setCustomPhotos(order.custom_photos);
        }
      }
      
      // Set upsells
      setRushDelivery(order.is_rush || false);
      setSongRequest(order.upsells?.includes("song_request") || false);
      if (order.song_details) setSongDetails(order.song_details);
      
      setResumeOrderId(orderId);
    } catch (err) {
      console.error("Failed to resume order:", err);
      // Order might be expired or already completed - continue with fresh checkout
    }
  };

  useEffect(() => {
    // Fetch packages and settings on mount
    (async () => {
      try {
        const [packagesRes, settingsRes] = await Promise.all([
          axios.get(`${API}/packages`),
          axios.get(`${API}/settings/public`)
        ]);
        setPackages(packagesRes.data);
        setOrdersPaused(settingsRes.data.orders_paused || false);
        setPauseMessage(settingsRes.data.pause_message || "We are currently not accepting new orders. Please check back soon!");
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    })();
    
    // Check if resuming an abandoned cart
    const resumeId = searchParams.get("resume");
    if (resumeId) {
      handleResumeOrder(resumeId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      setFormData(f => ({ ...f, customer_name: user.full_name, customer_email: user.email }));
    }
  }, [user]);

  // Auto-validate referral code from URL on load
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) {
      setReferralValidated(null);
      return;
    }
    setValidatingReferral(true);
    try {
      const res = await axios.get(`${API}/referral/validate/${code.toUpperCase()}`);
      setReferralValidated(res.data);
    } catch (err) {
      setReferralValidated(false);
    }
    setValidatingReferral(false);
  };

  const calculateTotal = () => {
    if (!packages) return 0;
    let total = 0;
    if (selectedPackage === "custom") {
      // Use tiered pricing
      const pricing = getCustomPricing(customPhotos);
      total = pricing ? pricing.total : customPhotos * packages.custom_base_price;
    } else {
      total = packages.packages[selectedPackage]?.price || 0;
    }
    if (rushDelivery) total += packages.upsells.rush_delivery.price;
    if (songRequest) total += packages.upsells.song_request.price;
    if (videoBook) {
      total += VIDEO_BOOK_PRICE; // Video Book add-on
      total += VIDEO_BOOK_SHIPPING; // Shipping for Video Book
    }
    // Apply referral discount
    if (referralValidated && referralValidated.discount_amount) {
      total = Math.max(0, total - referralValidated.discount_amount);
    }
    // Apply coupon discount
    if (couponValid && couponValid.discount_amount) {
      total = Math.max(0, total - couponValid.discount_amount);
    }
    return total;
  };

  const getSubtotal = () => {
    if (!packages) return 0;
    let total = 0;
    if (selectedPackage === "custom") {
      const pricing = getCustomPricing(customPhotos);
      total = pricing ? pricing.total : customPhotos * packages.custom_base_price;
    } else {
      total = packages.packages[selectedPackage]?.price || 0;
    }
    if (rushDelivery) total += packages.upsells.rush_delivery.price;
    if (songRequest) total += packages.upsells.song_request.price;
    if (videoBook) {
      total += VIDEO_BOOK_PRICE; // Video Book add-on
      total += VIDEO_BOOK_SHIPPING; // Shipping for Video Book
    }
    return total;
  };

  const handleCheckout = async () => {
    if (ordersPaused) {
      setError("Orders are currently paused. Please try again later.");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the terms and conditions");
      return;
    }
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      const errs = {};
      if (!formData.customer_name) errs.customer_name = true;
      if (!formData.customer_email) errs.customer_email = true;
      if (!formData.customer_phone) errs.customer_phone = true;
      setFieldErrors(errs);
      setError("Please fill in all required fields");
      // Scroll to first missing field
      const first = document.querySelector('[data-field-error="true"]');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Validate shipping address if Video Book is selected
    if (videoBook) {
      if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip) {
        const errs = {};
        if (!shippingAddress.street) errs.shipping_street = true;
        if (!shippingAddress.city) errs.shipping_city = true;
        if (!shippingAddress.state) errs.shipping_state = true;
        if (!shippingAddress.zip) errs.shipping_zip = true;
        setFieldErrors(errs);
        setError("Please fill in the complete shipping address for Video Book delivery");
        const first = document.querySelector('[data-field-error="true"]');
        if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    setFieldErrors({});
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        package_id: selectedPackage,
        custom_photos: selectedPackage === "custom" ? customPhotos : null,
        rush_delivery: rushDelivery,
        song_request: songRequest,
        song_details: songRequest ? songDetails : null,
        video_book: videoBook,
        video_book_shipping: videoBook ? VIDEO_BOOK_SHIPPING : null,
        shipping_address: videoBook ? shippingAddress : null,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        timezone: formData.timezone,
        origin_url: window.location.origin,
        referral_code: referralValidated ? referralCode.toUpperCase() : null,
        memorial_service_info: {
          funeral_home: funeralHome || null,
          arranger_name: arrangerName || null,
        },
        coupon_code: couponValid ? couponCode.toUpperCase().trim() : null,
      });

      // Redirect to Stripe
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create checkout session");
      setLoading(false);
    }
  };

  const photoLimit = selectedPackage === "custom" 
    ? customPhotos 
    : packages?.packages[selectedPackage]?.photos || 0;

  // Calculate custom pricing with tiered discounts
  const getCustomPricing = (photos) => {
    if (!packages?.custom_pricing_tiers || photos < (packages?.custom_min_photos || 5)) return null;
    
    // Fixed tier floors — custom must NEVER undercut these
    const TIER_FLOORS = {30: 750, 50: 1400, 75: 2250};
    
    for (const tier of packages.custom_pricing_tiers) {
      if (photos >= tier.min && photos <= tier.max) {
        const baseTotal = photos * packages.custom_base_price;
        let discountedTotal = photos * tier.price;
        
        // Enforce tier floor: never cheaper than fixed tier at same photo count
        if (TIER_FLOORS[photos] && discountedTotal < TIER_FLOORS[photos]) {
          discountedTotal = TIER_FLOORS[photos];
        }
        
        const effectivePerPhoto = Math.round((discountedTotal / photos) * 100) / 100;
        return {
          pricePerPhoto: effectivePerPhoto,
          tierLabel: tier.label,
          total: discountedTotal,
          savings: Math.max(0, baseTotal - discountedTotal),
          savingsPercent: Math.max(0, Math.round((1 - discountedTotal / baseTotal) * 100))
        };
      }
    }
    return null;
  };

  const customPricing = selectedPackage === "custom" ? getCustomPricing(customPhotos) : null;

  // If user is not logged in, show account required prompt
  if (!user) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4" data-testid="checkout-page">
        <div className="max-w-xl mx-auto">
          <BackButton to="/" label="Back to Home" />
          
          {/* Account Required Card */}
          <div className="card-dark p-8 text-center">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-yellow-500" />
            </div>
            
            <h1 className="text-3xl font-bold font-serif mb-4">Create Your Free Account</h1>
            <p className="text-zinc-400 mb-6 text-lg">
              Sign up to start your memorial video order. It only takes a minute and helps us keep you updated on your order status.
            </p>
            
            {/* Benefits List */}
            <div className="bg-zinc-800/50 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-yellow-500 mb-4 text-center">Why create an account?</h3>
              <ul className="space-y-3 text-zinc-300">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Track your order status in real-time</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Receive email notifications at every step</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Access your video from any device</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Request re-edits and communicate with our team</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Earn referral credits for future orders</span>
                </li>
              </ul>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-4">
              <Link 
                to="/signup?redirect=/checkout" 
                className="btn-primary w-full flex items-center justify-center gap-2 text-lg py-4"
                data-testid="checkout-signup-btn"
              >
                <Sparkles size={20} />
                Create Your Free Account
              </Link>
              
              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-zinc-700"></div>
                <span className="text-zinc-500 text-sm">Already have an account?</span>
                <div className="flex-1 border-t border-zinc-700"></div>
              </div>
              
              <Link 
                to="/login?redirect=/checkout" 
                className="block w-full bg-zinc-700 hover:bg-zinc-600 text-white py-3 px-6 rounded-lg font-semibold transition text-center"
                data-testid="checkout-login-btn"
              >
                Log In
              </Link>
            </div>
            
            <p className="text-xs text-zinc-500 mt-6">
              By creating an account, you agree to our{" "}
              <Link to="/terms" className="text-yellow-500 hover:underline">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-yellow-500 hover:underline">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        <BackButton to="/" label="Back to Home" />
        <h1 className="text-4xl font-bold font-serif text-center mb-8">Complete Your Order</h1>

        {/* Orders Paused Banner */}
        {ordersPaused && (
          <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-6 mb-8 text-center" data-testid="orders-paused-banner">
            <AlertCircle className="w-12 h-12 text-orange-400 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-orange-400 mb-2">Orders Currently Paused</h2>
            <p className="text-zinc-300">{pauseMessage}</p>
          </div>
        )}

        {/* Test Mode Banner */}
        {isTestMode && (
          <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4 mb-6" data-testid="test-mode-banner">
            <div className="flex items-center justify-center gap-3">
              <AlertCircle className="text-amber-400" size={20} />
              <div className="text-center">
                <p className="font-semibold text-amber-400">Test Mode Active</p>
                <p className="text-sm text-zinc-400">Use test card: 4242 4242 4242 4242, any future date, any CVC</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Left: Form */}
          <div className="space-y-6">
            {/* Package Selection */}
            <div className="card-dark p-6">
              <h2 className="text-xl font-semibold mb-4">Select Package</h2>
              {packages && (
                <div className="space-y-3">
                  {Object.entries(packages.packages).map(([id, pkg]) => (
                    <label
                      key={id}
                      className={`block p-4 rounded-lg border cursor-pointer transition ${
                        selectedPackage === id ? 'border-yellow-500 bg-yellow-500/10' : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid={`checkout-package-${id}`}
                    >
                      <input
                        type="radio"
                        name="package"
                        value={id}
                        checked={selectedPackage === id}
                        onChange={() => setSelectedPackage(id)}
                        className="sr-only"
                      />
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-semibold">{pkg.name}</div>
                          <div className="text-sm text-zinc-400">{pkg.photos} photos • {pkg.video_duration}</div>
                        </div>
                        <div className="text-xl font-bold text-yellow-500">${pkg.price.toLocaleString()}</div>
                      </div>
                    </label>
                  ))}
                  <label
                    className={`block p-4 rounded-lg border cursor-pointer transition ${
                      selectedPackage === "custom" ? 'border-yellow-500 bg-yellow-500/10' : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="checkout-package-custom"
                  >
                    <input
                      type="radio"
                      name="package"
                      value="custom"
                      checked={selectedPackage === "custom"}
                      onChange={() => setSelectedPackage("custom")}
                      className="sr-only"
                    />
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold">Custom Package</div>
                        <div className="text-sm text-zinc-400">
                          Starting at ${packages.custom_base_price}/photo
                          <span className="text-green-400 ml-2">• Volume discounts available!</span>
                        </div>
                      </div>
                    </div>
                  </label>
                  {selectedPackage === "custom" && (
                    <div className="ml-4 mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                      <label className="block text-sm mb-2 font-medium">Number of Photos</label>
                      <div className="flex items-center gap-2">
                        {/* Decrement Button */}
                        <button
                          type="button"
                          onClick={() => setCustomPhotos(prev => Math.max(packages.custom_min_photos, prev - 1))}
                          className="w-10 h-10 flex items-center justify-center bg-zinc-700 hover:bg-zinc-600 border border-zinc-600 rounded-lg transition-colors"
                          disabled={customPhotos <= packages.custom_min_photos}
                          data-testid="custom-photos-decrement"
                        >
                          <ChevronDown size={20} className={customPhotos <= packages.custom_min_photos ? 'text-zinc-500' : 'text-white'} />
                        </button>
                        
                        {/* Number Input - allows free typing */}
                        <input
                          type="number"
                          min={packages.custom_min_photos}
                          value={customPhotos}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow empty or any number while typing
                            if (val === '' || val === '0') {
                              setCustomPhotos(packages.custom_min_photos);
                            } else {
                              setCustomPhotos(parseInt(val) || packages.custom_min_photos);
                            }
                          }}
                          onBlur={(e) => {
                            // Enforce minimum only on blur (when done typing)
                            const val = parseInt(e.target.value) || 0;
                            if (val < packages.custom_min_photos) {
                              setCustomPhotos(packages.custom_min_photos);
                            }
                          }}
                          className="w-20 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-lg font-semibold text-center focus:border-yellow-500 focus:outline-none"
                          data-testid="custom-photos-input"
                        />
                        
                        {/* Increment Button */}
                        <button
                          type="button"
                          onClick={() => setCustomPhotos(prev => prev + 1)}
                          className="w-10 h-10 flex items-center justify-center bg-zinc-700 hover:bg-zinc-600 border border-zinc-600 rounded-lg transition-colors"
                          data-testid="custom-photos-increment"
                        >
                          <ChevronUp size={20} className="text-white" />
                        </button>
                        
                        <span className="text-zinc-400 text-sm ml-2">photos (min: {packages.custom_min_photos})</span>
                        
                        {/* Real-time Price Display */}
                        {customPricing && (
                          <div className="ml-auto flex items-center gap-2">
                            <span className="text-2xl font-bold text-yellow-500" data-testid="custom-realtime-price">
                              ${customPricing.total.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Tiered Pricing Display */}
                      {customPricing && (
                        <div className="mt-4 space-y-3">
                          {/* Price Tier Badge */}
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              customPricing.savingsPercent >= 20 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                                : customPricing.savingsPercent > 0 
                                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                                  : 'bg-zinc-700 text-zinc-300'
                            }`}>
                              {customPricing.tierLabel}
                            </span>
                            <span className="text-zinc-400 text-sm">${customPricing.pricePerPhoto}/photo</span>
                          </div>
                          
                          {/* Savings Display */}
                          {customPricing.savings > 0 && (
                            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-3">
                              <div className="flex items-center gap-2 text-green-400">
                                <Sparkles size={18} />
                                <span className="font-semibold">You&apos;re saving ${customPricing.savings.toFixed(0)}!</span>
                                <span className="text-green-300 text-sm">({customPricing.savingsPercent}% off)</span>
                              </div>
                              <p className="text-green-300/70 text-xs mt-1">
                                Compared to standard ${packages.custom_base_price}/photo pricing
                              </p>
                            </div>
                          )}
                          
                          {/* Pricing Tiers Info */}
                          <div className="text-xs text-zinc-500 space-y-1 pt-2 border-t border-zinc-700">
                            <p className="font-medium text-zinc-400 mb-1">Volume Discounts:</p>
                            {packages.custom_pricing_tiers?.map((tier, i) => (
                              <div key={i} className={`flex justify-between ${customPhotos >= tier.min && customPhotos <= tier.max ? 'text-yellow-400' : ''}`}>
                                <span>{tier.min}-{tier.max === 999 ? '∞' : tier.max} photos</span>
                                <span>${tier.price}/photo {tier.price < packages.custom_base_price && `(${Math.round((1-tier.price/packages.custom_base_price)*100)}% off)`}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Upsells */}
            <div className="card-dark p-6">
              <h2 className="text-xl font-semibold mb-4">Add-ons</h2>
              <div className="space-y-4">
                {/* Video Book Add-on */}
                <div className={`rounded-lg border transition-colors ${videoBook ? 'border-yellow-500 bg-yellow-500/10' : 'border-yellow-500/30 bg-yellow-500/5 hover:bg-yellow-500/10'}`}>
                  <label className="flex items-start gap-3 cursor-pointer p-3">
                    <input
                      type="checkbox"
                      checked={videoBook}
                      onChange={(e) => setVideoBook(e.target.checked)}
                      className="w-5 h-5 accent-yellow-500 mt-1"
                      data-testid="video-book-checkbox"
                    />
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        <BookOpen size={18} className="text-yellow-500" />
                        Premium Video Book
                      </div>
                      <div className="text-sm text-zinc-400">Physical book with built-in HD screen that plays your memorial video when opened</div>
                    </div>
                    <div className="text-right">
                      <div className="text-yellow-500 font-semibold">+$500</div>
                      <div className="text-xs text-zinc-400">+$25 shipping</div>
                    </div>
                  </label>
                  
                  {/* Shipping Address Section - Expands when Video Book is selected */}
                  {videoBook && (
                    <div className="px-3 pb-4 pt-2 border-t border-yellow-500/30 mt-2">
                      <div className="flex items-center gap-2 mb-3">
                        <Truck size={16} className="text-yellow-500" />
                        <span className="text-sm font-medium text-yellow-500">Shipping Address for Video Book Delivery</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-zinc-400 mb-1">Street Address *</label>
                          <input
                            type="text"
                            value={shippingAddress.street}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                            placeholder="123 Main Street, Apt 4B"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500"
                            data-testid="shipping-street"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-zinc-400 mb-1">City *</label>
                            <input
                              type="text"
                              value={shippingAddress.city}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                              placeholder="New York"
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500"
                              data-testid="shipping-city"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-zinc-400 mb-1">State *</label>
                            <input
                              type="text"
                              value={shippingAddress.state}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                              placeholder="NY"
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500"
                              data-testid="shipping-state"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-zinc-400 mb-1">ZIP Code *</label>
                            <input
                              type="text"
                              value={shippingAddress.zip}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value })}
                              placeholder="10001"
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500"
                              data-testid="shipping-zip"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-zinc-400 mb-1">Country</label>
                            <input
                              type="text"
                              value={shippingAddress.country}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500"
                              data-testid="shipping-country"
                            />
                          </div>
                        </div>
                        <div className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                          <Package size={12} />
                          <span>Package weight: 1.5 lbs • Standard shipping (5-7 business days)</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Delivery Speed Section */}
                <div className="space-y-3 mt-4 pt-4 border-t border-zinc-700/50">
                  <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
                    <Clock size={16} className="text-yellow-500" />
                    Delivery Speed
                  </h3>
                  
                  {/* Standard Delivery - Always Selected by Default */}
                  <div className={`p-3 rounded-lg border-2 transition-all ${!rushDelivery ? 'border-yellow-500 bg-yellow-500/10' : 'border-zinc-700 bg-zinc-800/50'}`}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="delivery_speed"
                        checked={!rushDelivery}
                        onChange={() => setRushDelivery(false)}
                        className="w-4 h-4 accent-yellow-500"
                        data-testid="standard-delivery-radio"
                      />
                      <div className="flex-1">
                        <div className="font-medium">Standard Delivery</div>
                        <div className="text-sm text-zinc-400">Est. {getEstimatedDelivery(false)}</div>
                      </div>
                      <div className="text-zinc-400 font-medium">Included</div>
                    </label>
                  </div>
                  
                  {/* Rush Delivery Option */}
                  <div className={`p-3 rounded-lg border-2 transition-all ${rushDelivery ? 'border-orange-500 bg-orange-500/10' : 'border-zinc-700 bg-zinc-800/50'}`}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="delivery_speed"
                        checked={rushDelivery}
                        onChange={() => setRushDelivery(true)}
                        className="w-4 h-4 accent-orange-500"
                        data-testid="rush-delivery-radio"
                      />
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-2">
                          <Zap size={14} className="text-orange-400" />
                          48-Hour Rush Delivery
                        </div>
                        <div className="text-sm text-zinc-400">Est. {getEstimatedDelivery(true)}</div>
                      </div>
                      <div className="text-orange-400 font-semibold">+$299</div>
                    </label>
                  </div>
                </div>
                
                <label className="flex items-start gap-3 cursor-pointer mt-4">
                  <input
                    type="checkbox"
                    checked={songRequest}
                    onChange={(e) => setSongRequest(e.target.checked)}
                    className="w-5 h-5 accent-yellow-500 mt-1"
                    data-testid="song-request-checkbox"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Specific Song Request</div>
                    <div className="text-sm text-zinc-400">Choose a specific song for your video</div>
                    {songRequest && (
                      <input
                        type="text"
                        placeholder="Song title and artist..."
                        value={songDetails}
                        onChange={(e) => setSongDetails(e.target.value)}
                        className="mt-2 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-sm"
                        data-testid="song-details-input"
                      />
                    )}
                  </div>
                  <div className="text-yellow-500 font-semibold">+$50</div>
                </label>
              </div>
            </div>

            {/* Customer Info */}
            <div className="card-dark p-6">
              <h2 className="text-xl font-semibold mb-4">Your Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => { setFormData({ ...formData, customer_name: e.target.value }); if (fieldErrors.customer_name) setFieldErrors(p => ({...p, customer_name: false})); }}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500 ${fieldErrors.customer_name ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700'}`}
                    data-field-error={fieldErrors.customer_name ? "true" : undefined}
                    required
                    data-testid="checkout-name"
                  />
                  {fieldErrors.customer_name && <p className="text-red-400 text-xs mt-1">Name is required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => { setFormData({ ...formData, customer_email: e.target.value }); if (fieldErrors.customer_email) setFieldErrors(p => ({...p, customer_email: false})); }}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500 ${fieldErrors.customer_email ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700'}`}
                    data-field-error={fieldErrors.customer_email ? "true" : undefined}
                    required
                    data-testid="checkout-email"
                  />
                  {fieldErrors.customer_email && <p className="text-red-400 text-xs mt-1">Email is required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => { setFormData({ ...formData, customer_phone: e.target.value }); if (fieldErrors.customer_phone) setFieldErrors(p => ({...p, customer_phone: false})); }}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500 ${fieldErrors.customer_phone ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700'}`}
                    data-field-error={fieldErrors.customer_phone ? "true" : undefined}
                    required
                    data-testid="checkout-phone"
                  />
                  {fieldErrors.customer_phone && <p className="text-red-400 text-xs mt-1">Phone is required</p>}
                </div>
              </div>

              {/* Service Information */}
              <div className="mt-6 pt-6 border-t border-zinc-700">
                <label className="block text-sm font-medium mb-4">
                  Service Details
                </label>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">What funeral home is handling the service?</label>
                    <input
                      type="text"
                      placeholder="Funeral home name"
                      value={funeralHome}
                      onChange={(e) => setFuneralHome(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500"
                      data-testid="funeral-home-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">Who is your arranger? (First & last name)</label>
                    <input
                      type="text"
                      placeholder="Arranger's full name"
                      value={arrangerName}
                      onChange={(e) => setArrangerName(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500"
                      data-testid="arranger-name-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Order Summary — clean, minimal */}
          <div>
            <div className="card-dark p-6 sticky top-24">
              <h2 className="text-xl font-semibold mb-5">Order Summary</h2>
              
              {/* Selected Package */}
              <div className="mb-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Selected Package</p>
                <p className="font-semibold text-lg">
                  {selectedPackage === "custom" 
                    ? `Custom Package — ${customPhotos} photos` 
                    : `${packages?.packages[selectedPackage]?.name} — Memorial Video`}
                </p>
              </div>

              {/* Price Breakdown */}
              <div className="space-y-2.5 mb-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Price Breakdown</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">
                    {selectedPackage === "custom" 
                      ? `${customPhotos} photos` 
                      : packages?.packages[selectedPackage]?.name}
                  </span>
                  <span>
                    ${selectedPackage === "custom" 
                      ? (customPricing ? customPricing.total.toLocaleString() : (customPhotos * (packages?.custom_price_per_photo || 25)).toLocaleString())
                      : packages?.packages[selectedPackage]?.price.toLocaleString()}
                  </span>
                </div>
                {rushDelivery && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Rush Delivery</span>
                    <span>$299</span>
                  </div>
                )}
                {songRequest && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Song Request</span>
                    <span>$50</span>
                  </div>
                )}
                {videoBook && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Premium Video Book</span>
                      <span>$500</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Shipping</span>
                      <span>$25</span>
                    </div>
                  </>
                )}
                {!rushDelivery && !songRequest && !videoBook && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Add-ons</span>
                    <span className="text-zinc-500">$0</span>
                  </div>
                )}
                {referralValidated && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Referral Discount</span>
                    <span>-${referralValidated.discount_amount}</span>
                  </div>
                )}
                {couponValid && couponValid.valid && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Coupon ({couponCode})</span>
                    <span>-${couponValid.discount_amount}</span>
                  </div>
                )}

                <div className="border-t border-zinc-700 pt-3 mt-1 flex justify-between">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-2xl font-bold text-yellow-500">${calculateTotal().toLocaleString()}</span>
                </div>
              </div>

              {/* Delivery */}
              <div className="mb-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Delivery</p>
                <p className="text-sm text-zinc-300">
                  {rushDelivery ? '48-hour rush delivery' : 'Standard delivery (5–7 business days)'}
                </p>
              </div>

              {/* Next Steps */}
              <p className="text-xs text-zinc-500 mb-5">
                After payment, you'll upload your photos and we'll begin editing.
              </p>

              {/* Coupon Code */}
              <div className="mb-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Coupon Code</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); }}
                    placeholder="Enter code"
                    className="flex-1 bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-sm text-white placeholder-zinc-500"
                    data-testid="coupon-input"
                  />
                  <button
                    onClick={async () => {
                      if (!couponCode.trim()) return;
                      setCouponChecking(true);
                      try {
                        const r = await axios.get(`${API}/coupons/validate/${couponCode.trim()}?vertical=memorial`);
                        setCouponValid(r.data);
                      } catch (e) {
                        setCouponValid({ valid: false, message: e.response?.data?.detail || "Invalid code" });
                      }
                      setCouponChecking(false);
                    }}
                    disabled={couponChecking || !couponCode.trim()}
                    className="px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded text-sm font-medium disabled:opacity-40"
                    data-testid="coupon-apply-btn"
                  >
                    {couponChecking ? "..." : "Apply"}
                  </button>
                </div>
                {couponValid && couponValid.valid && (
                  <p className="text-green-400 text-xs mt-1">✓ ${couponValid.discount_amount} discount applied!</p>
                )}
                {couponValid && !couponValid.valid && (
                  <p className="text-red-400 text-xs mt-1">{couponValid.message}</p>
                )}
              </div>

              {/* Terms */}
              <label className="flex items-start gap-2 mb-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="w-5 h-5 accent-yellow-500 mt-0.5"
                  data-testid="terms-checkbox"
                />
                <span className="text-sm text-zinc-400">
                  I agree to the <Link to="/terms?vertical=memorial" className="text-yellow-500 hover:text-yellow-400">Terms of Service</Link> and <Link to="/privacy?vertical=memorial" className="text-yellow-500 hover:text-yellow-400">Privacy Policy</Link>
                </span>
              </label>

              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-3 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleCheckout}
                disabled={loading}
                className="btn-primary w-full text-lg"
                data-testid="checkout-submit"
              >
                {loading ? "Processing..." : "Let's Create Your Video →"}
              </button>

              <p className="text-xs text-zinc-500 text-center mt-3">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== REAL ESTATE CHECKOUT PAGE (UPGRADED) ==============

export default CheckoutPage;
